import { ViewState } from 'react-map-gl/mapbox';
import { atom, selector, selectorFamily } from 'recoil';
import { PointFeature } from 'supercluster';
import { DEFAULT_BOTTOM_CARD_HEIGHT, TIMELINE_WINDOW_HEIGHT, VIDEO_REQUEST_WINDOW_WIDTH } from '../constants/app';
import { MAP_CONFIGURATION, ROAD_MAP_TYPE } from '../constants/map';
import { BottomCard, MapDrawMode, MapType, RightCard } from '../types/app';
import { MapCurrentState } from '../types/currentState';
import { GeoPlace } from '../types/geoModels';
import { EventCore, LastAlarm } from '../types/gptTypes';
import { localStorageEffect, persistedAtom } from '../utils/persistence';
import { bottomCardHeightState, floatingCardsState, pageState, rigthCardWidthState, topCardHeightState } from './app';
import { currentStatesState } from './client';
import { eventDetailState, eventsState } from './gptStates';
import { pathState } from './reports';
import { capturingState, showPlaybackStreamingState, timelineEvidenceEventsState } from './streaming';

export const navCtrl = atom<boolean>({
  key: 'app.map.controls.navigation.active',
  default: false,
});

export const mapTypeState = persistedAtom<MapType>('app.map.type', ROAD_MAP_TYPE);
export const mapStyleState = selector({
  key: 'app.map.styles',
  get: ({ get }) => MAP_CONFIGURATION[get(mapTypeState)].style,
});
export const leftMapPaddingState = selector<number>({
  key: 'app.map.padding.left',
  get: ({ get }) => {
    const floatingCards = get(floatingCardsState);
    const { hasList, hasContent, listIsExpanded, contentIsExpanded, contentWidth, listWidth } = floatingCards;
    const isCapturingState = !!get(capturingState).ident;
    let padding = isCapturingState ? VIDEO_REQUEST_WINDOW_WIDTH + 24 : 0;

    if ((!hasList && !hasContent) || (!listIsExpanded && !contentIsExpanded)) {
      return padding;
    }

    if (hasList && hasContent) {
      if (listIsExpanded && contentIsExpanded) {
        padding += listWidth + contentWidth + 24;
      } else if (contentIsExpanded) {
        padding += contentWidth + 12;
      }
    } else if (hasList && listIsExpanded) {
      padding += listWidth + 12;
    } else if (hasContent && contentIsExpanded) {
      padding += contentWidth + 12;
    }

    return padding;
  },
});

export const rightMapPaddingState = selector({
  key: 'app.map.padding.right',
  get: ({ get }) => {
    const showingPlayback = get(showPlaybackStreamingState);
    const rightCardWidth = showingPlayback
      ? get(rigthCardWidthState(RightCard.playback))
      : get(rigthCardWidthState(RightCard.streaming));
    return rightCardWidth ? rightCardWidth + 12 : 0;
  },
});

export const bottomMapPaddingState = selector({
  key: 'app.map.padding.bottom',
  get: ({ get }) => {
    const isCapturingState = !!get(capturingState).ident;
    if (isCapturingState) {
      return 28 + TIMELINE_WINDOW_HEIGHT;
    }
    const hasSelectedPath = !!get(pathState);
    if (hasSelectedPath) {
      return 28 + get(bottomCardHeightState(BottomCard.pathInfo));
    }
    return 0;
  },
});
export const topMapPaddingState = selector({
  key: 'app.map.padding.top',
  get: ({ get }) => {
    const isCapturingState = !!get(capturingState).ident;
    const hasEvents = !!get(timelineEvidenceEventsState);
    const topIsOpen = isCapturingState && hasEvents;
    const topCardHeight = topIsOpen ? get(topCardHeightState('events')) : 0;
    return topIsOpen ? 28 + topCardHeight : 0;
  },
});

export const mapViewState = atom<ViewState>({
  key: 'map.view',
  default: {
    longitude: -100.38806,
    latitude: 20.58806,
    zoom: 14,
  } as ViewState,
  effects: [localStorageEffect],
});

export const initialCenteringState = atom<boolean>({
  key: 'map.view.initialCentering',
  default: true,
});

export const showTrafficLayerState = persistedAtom('app.map.layer.traffic.show', false);
export const showFencesLayerState = persistedAtom('app.map.layer.fences.show', false);
export const showDeliveryFencesLayerState = persistedAtom('app.map.layer.deliveryFences.show', false);

export const spotsGeojsonState = atom<{ [key: string]: GeoJSON.FeatureCollection } | undefined>({
  key: 'app.map.layer.spots.geojson',
  default: undefined,
});

export const selectedSpotLayerState = persistedAtom<string | undefined>('app.map.layer.spot.selected', undefined);

export const zoneFencesGeojsonState = atom<{ [key: string]: GeoJSON.FeatureCollection } | undefined>({
  key: 'app.map.layer.zoneFences.geojson',
  default: undefined,
});

export const selectedZoneFenceState = persistedAtom<string | undefined>('app.map.layer.zoneFence.selected', undefined);

export const interactiveLayersState = selector({
  key: 'app.map.interactiveLayers',
  get: ({ get }) => {
    const interactiveLayers: string[] = ['route-event'];
    const spotsGeojson = get(spotsGeojsonState);
    const selectedSpot = get(selectedSpotLayerState);

    if (spotsGeojson && selectedSpot && !!spotsGeojson[selectedSpot]) interactiveLayers.push('spots-layer');

    return interactiveLayers;
  },
});

export const fencesGeojsonState = persistedAtom<GeoJSON.FeatureCollection | undefined>(
  'app.map.layer.fences.geojson',
  undefined,
);

export const selectedFenceIdState = atom<string | undefined>({
  key: 'app.map.layer.fences.selected',
  default: undefined,
});

export const selectedFenceGeojsonState = selector<GeoJSON.FeatureCollection | undefined>({
  key: 'app.map.layer.fences.selected.geojson',
  get: ({ get }) => {
    const fenceId = get(selectedFenceIdState);
    if (!fenceId) return;
    const fencesGeojson = get(fencesGeojsonState);
    if (!fencesGeojson) return;
    const fenceFeature = fencesGeojson.features.find((feature) => feature.properties?.id === fenceId);
    if (!fenceFeature) return;
    return {
      ...fencesGeojson,
      features: [fenceFeature],
    };
  },
});

export const updatedFencesKeyState = atom<string | undefined>({
  key: 'app.map.layer.fences.key',
  default: undefined,
});

export const deliveryFencesGeojsonState = persistedAtom<GeoJSON.FeatureCollection | undefined>(
  'app.map.layer.deliveryFences.geojson',
  undefined,
);

export const selectedDeliveryFenceIdState = atom<string | undefined>({
  key: 'app.map.layer.deliveryFences.selected',
  default: undefined,
});

export const selectedDeliveryFenceGeojsonState = selector<GeoJSON.FeatureCollection | undefined>({
  key: 'app.map.layer.deliveryFences.selected.geojson',
  get: ({ get }) => {
    const deliveryFenceId = get(selectedDeliveryFenceIdState);
    if (!deliveryFenceId) return;
    const deliveryFencesGeojson = get(deliveryFencesGeojsonState);
    if (!deliveryFencesGeojson) return;
    const fenceFeature = deliveryFencesGeojson.features.find((feature) => feature.properties?.id === deliveryFenceId);
    if (!fenceFeature) return;
    return {
      ...deliveryFencesGeojson,
      features: [fenceFeature],
    };
  },
});

export const updatedDeliveryFencesKeyState = atom<string | undefined>({
  key: 'app.map.layer.deliveryFences.key',
  default: undefined,
});

export const mapCurrentStatesState = atom<MapCurrentState[] | undefined>({
  key: 'app.map.currentStates',
  default: undefined,
});

export const mapCurrentStatesGeoJson = selector<
  PointFeature<{ cluster: boolean; ident: string; point_count: number }>[]
>({
  key: 'app.map.currentStates.geojson',
  get: ({ get }) => {
    let mapCurrentStates = get(mapCurrentStatesState);
    if (!mapCurrentStates) return [];

    const assetIdsInView = get(assetIdsInViewState);
    if (assetIdsInView?.length) {
      mapCurrentStates = mapCurrentStates.filter((mapCurrentState) => assetIdsInView.includes(mapCurrentState.assetId));
    }

    return mapCurrentStates.map((mapCurrentState) => {
      let feature: PointFeature<{ cluster: boolean; ident: string; point_count: number }> = {
        type: 'Feature',
        properties: { cluster: false, ident: mapCurrentState.ident, point_count: 0 },
        geometry: mapCurrentState.point as GeoJSON.Point,
      };
      return feature;
    });
  },
});

export const assetIdsInViewState = atom<string[] | undefined>({
  key: 'app.map.assetIds.visible',
  default: undefined,
});

export const assetsInViewState = selector({
  key: 'app.map.assets.visible',
  get: ({ get }) => {
    const assetIds = get(assetIdsInViewState);
    if (!assetIds?.length) return;

    const currentStates = get(currentStatesState);
    if (!currentStates) return;

    return currentStates.filter((currentState) => assetIds.includes(currentState.asset._id));
  },
});

export const mapCurrentStateState = selectorFamily<MapCurrentState | undefined, string>({
  key: 'app.map.currentState',
  get:
    (ident) =>
    ({ get }) => {
      const mapCurrentStates = get(mapCurrentStatesState);
      if (!mapCurrentStates) return;
      return mapCurrentStates.find((mapCurrentState) => mapCurrentState.ident === ident);
    },
});

export const mapDrawModeState = atom<MapDrawMode | undefined>({
  key: 'app.map.drawMode',
  default: undefined,
});

export const mapNewFeatureState = atom<GeoJSON.Feature | undefined>({
  key: 'app.map.newFeature',
  default: undefined,
});

export const mapNewCircleState = atom<{ lat: number; lng: number; radius: number } | undefined>({
  key: 'app.map.newCircle',
  default: undefined,
});

export const mapGptEventsGeoJsonState = selector<
  PointFeature<{ cluster: boolean; point_count: number; alarm: LastAlarm; event: EventCore }>[]
>({
  key: 'app.map.gpt.events.geojson',
  get: ({ get }) => {
    const events = get(eventsState);

    if (!events?.items) return [];

    const items = events.items.map((event) => ({ alarm: event.lastAlarm, event }));
    const alarmMarkers = items.map((item) => ({
      ...item,
      geometry: {
        type: 'Point',
        coordinates: [item.alarm.gpsLng ?? item.alarm.gpsLng, item.alarm.gpsLat ?? item.alarm.gpsLat],
      },
    }));

    return alarmMarkers.map((alarmMarker) => {
      const feature: PointFeature<{ cluster: boolean; point_count: number; alarm: LastAlarm; event: EventCore }> = {
        type: 'Feature',
        properties: { cluster: false, alarm: alarmMarker.alarm, event: alarmMarker.event, point_count: 0 },
        geometry: alarmMarker.geometry as GeoJSON.Point,
      };
      return feature;
    });
  },
});

export const mapGptEventAlarmsGeoJsonState = selector<GeoJSON.Feature[]>({
  key: 'app.map.gpt.alarms.geojson',
  get: ({ get }) => {
    const event = get(eventDetailState);

    if (!event) return [];

    const { alarms } = event;

    if (!alarms) return [];

    const alarmMarkers = alarms.map((alarm) => ({
      alarm,
      geometry: {
        type: 'Point',
        coordinates: [alarm.gpsLng ?? alarm.gpsLng, alarm.gpsLat ?? alarm.gpsLat],
      },
    }));

    return alarmMarkers.map(
      (alarmMarker) =>
        ({
          type: 'Feature',
          properties: { cluster: false, alarm: alarmMarker.alarm },
          geometry: alarmMarker.geometry,
        }) as GeoJSON.Feature,
    );
  },
});

export const temporaryPositionMarkerState = atom<GeoPlace | undefined>({
  key: 'app.map.markers.temporary',
  default: undefined,
});
